/**
 * Runtime-конфигурация. Приоритет: `window.__NBR_ENV__` (из `/env.js`, генерируется контейнером)
 * → `import.meta.env.VITE_*` (dev/.env) → значения по умолчанию.
 */
export type NbrEnv = {
  API_BASE_URL: string
  SPARNATURAL_CONFIG_URL: string
  SPARQL_ENDPOINT: string
  PUBLIC_BASE_URL: string
  SITE_TITLE: string
}

declare global {
  interface Window {
    __NBR_ENV__?: Partial<Record<keyof NbrEnv, string>>
  }
}

const DEFAULTS: NbrEnv = {
  API_BASE_URL: "/api",
  SPARNATURAL_CONFIG_URL: "/ontology/sparnatural.shacl.ttl",
  SPARQL_ENDPOINT: "",
  PUBLIC_BASE_URL: "",
  SITE_TITLE: "Национальный библиографический ресурс",
}

const trimSlash = (value: string) => value.replace(/\/+$/, "")

const pick = (
  runtime: Partial<Record<keyof NbrEnv, string>> | undefined,
  viteEnv: Record<string, string | undefined>,
  key: keyof NbrEnv,
): string => {
  const fromRuntime = runtime?.[key]
  if (typeof fromRuntime === "string" && fromRuntime.trim() !== "") return fromRuntime.trim()
  const fromVite = viteEnv[`VITE_${key}`]
  if (typeof fromVite === "string" && fromVite.trim() !== "") return fromVite.trim()
  return DEFAULTS[key]
}

/**
 * Собрать конфиг из источников. Экспортирована отдельно, чтобы тестировать без глобального `window`.
 */
export const resolveEnv = (
  runtime: Partial<Record<keyof NbrEnv, string>> | undefined,
  viteEnv: Record<string, string | undefined> = {},
): NbrEnv => {
  const apiBase = trimSlash(pick(runtime, viteEnv, "API_BASE_URL"))
  const sparql = pick(runtime, viteEnv, "SPARQL_ENDPOINT")
  return {
    API_BASE_URL: apiBase,
    SPARNATURAL_CONFIG_URL: pick(runtime, viteEnv, "SPARNATURAL_CONFIG_URL"),
    SPARQL_ENDPOINT: sparql || `${apiBase}/v1/sparql`,
    PUBLIC_BASE_URL: trimSlash(pick(runtime, viteEnv, "PUBLIC_BASE_URL")),
    SITE_TITLE: pick(runtime, viteEnv, "SITE_TITLE"),
  }
}

const readViteEnv = (): Record<string, string | undefined> => {
  try {
    return import.meta.env as unknown as Record<string, string | undefined>
  } catch {
    return {}
  }
}

export const env: NbrEnv = resolveEnv(
  typeof window !== "undefined" ? window.__NBR_ENV__ : undefined,
  readViteEnv(),
)

/** Базовый URL API с версией: `${API_BASE_URL}/v1`. */
export const apiV1BaseUrl = `${env.API_BASE_URL}/v1`

/** Абсолютный URL публичной страницы (для permalink / canonical). */
export const publicUrl = (path: string) => {
  const origin = env.PUBLIC_BASE_URL || (typeof window !== "undefined" ? window.location.origin : "")
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`
}
